// https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Optimizing_canvas
// https://stackoverflow.com/questions/19764018/controlling-fps-with-requestanimationframe
import log    from '@common/Logger/log'
import DOM    from '@common/DOM/DOM'
import { is } from '@common/is/is'

import createCanvas          from './createCanvas'
import { ECanvasEvent }      from './definitions'
import type { ICanvasProps } from './definitions'

const EXISTENCE_CHECK_AT = 60
const DEFAULT_FPS        = 24

/**
 * Canvas komponensünk.
 * @param props - Létrehozni kívánt canvas paraméterei {@link ICanvasProps}.
 */
export default function Canvas (props: ICanvasProps): HTMLCanvasElement {
  const res = createCanvas(props)

  if (is.null(res)) {
    return <div />
  }

  const { canvas, ctx } = res

  const fpsInterval = 1000 / (props.fps ?? DEFAULT_FPS)
  let lastTime = Date.now()

  let existenceCheck = 0

  let requestAnimationFrameId: number

  /**
   * Animáció indítása.
   * @see http://gameprogrammingpatterns.com/game-loop.html
   */
  function animate (): void {
    existenceCheck++

    if (existenceCheck >= EXISTENCE_CHECK_AT) {
      if (!document.body.contains(canvas)) {
        log('[Canvas] eltávolítva, rAF leállítva')

        return
      }

      existenceCheck = 0
    }

    if (!canvas.hasAttribute('pause')) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/requestAnimationFrame
      requestAnimationFrameId = requestAnimationFrame(animate)
    }

    // --- Eltelt idő kiszámítása az előző renderelés óta

    // https://coderwall.com/p/iygcpa/gameloop-the-correct-way
    const currentTime = Date.now()

    const deltaTime   = is.undefined(props.fps)
      ? (currentTime - lastTime) / 1000
      : currentTime - lastTime

    if (props.update) {
      props.update(deltaTime * 1000)
    }

    if (is.undefined(props.fps)) {
      props.render(ctx)

      lastTime = currentTime
    }
    // FPSLimit: Ha elég idő eltelt, akkor renderelj
    else if (deltaTime > fpsInterval) {
      props.render(ctx)

      lastTime = currentTime - (deltaTime % fpsInterval)
    }
  }

  /** Ne kezdjük el egyből az animate-et hívni, mert abban a pillanatban még nincs parentje és úgy érzékeli, mintha eltávolítottuk volna... */
  function checkAppended (): void {
    requestAnimationFrame(() => {
      if (DOM.isAttachedToDOM(canvas)) {
        animate()

        return
      }

      checkAppended()
    })
  }

  // Csak akkor kell a folyamatos animate, ha NEM statikus canvast akarunk.

  if (is.undefined(props.isStatic) || !props.isStatic) {
    checkAppended()
  }

  /** Animáció szüneteltetése. */
  function pause (): void {
    canvas.setAttribute('pause', '')

    // Lelőjjük az aktuális requestAnimationFrame-t.
    cancelAnimationFrame(requestAnimationFrameId)
  }

  /** Animáció folytatása. */
  function resume (): void {
    if (!canvas.hasAttribute('pause')) {
      return
    }

    canvas.removeAttribute('pause')

    lastTime = Date.now()

    animate()
  }

  canvas.addEventListener(ECanvasEvent.Pause, pause)
  canvas.addEventListener(ECanvasEvent.Resume, resume)

  return canvas
}
